/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-25
 */

import { useEffect } from 'react';
import { useGetSubscriptionStatusQuery } from '../features/auth/authApi';
import type { TrialStatus } from '../types/subscription';

/**
 * Hook for managing trial status and expiry warnings
 * Connects to backend /api/v1/subscription/trial/ endpoints
 */
export const useTrialStatus = () => {
  const {
    data: subscriptionData,
    isLoading,
    error,
    refetch,
  } = useGetSubscriptionStatusQuery();

  // Extract trial information from subscription data
  const trialStatus: TrialStatus | null =
    subscriptionData?.trial_status || null;

  // Helper functions for trial state
  const isTrialActive = Boolean(trialStatus?.active);
  const isTrialExpired = Boolean(trialStatus?.is_expired);
  const daysRemaining = trialStatus?.days_remaining || 0;
  const trialEndDate = trialStatus?.expires_at
    ? new Date(trialStatus.expires_at)
    : null;
  const planName = trialStatus?.plan || '';

  // Warning levels based on days remaining
  const getWarningLevel = (): 'none' | 'warning' | 'urgent' | 'expired' => {
    if (!isTrialActive) return 'none';
    if (isTrialExpired) return 'expired';
    if (daysRemaining <= 1) return 'urgent';
    if (daysRemaining <= 3) return 'warning';
    return 'none';
  };

  const warningLevel = getWarningLevel();

  // Warning messages
  const getWarningMessage = (): string => {
    if (!isTrialActive) return '';
    if (isTrialExpired)
      return 'Your trial has expired. Please upgrade to continue using premium features.';
    if (daysRemaining === 1)
      return `Your ${planName} trial expires tomorrow. Upgrade now to keep your features.`;
    if (daysRemaining <= 3)
      return `Your ${planName} trial expires in ${daysRemaining} days. Upgrade to continue.`;
    return '';
  };

  const warningMessage = getWarningMessage();

  // Show upgrade prompt conditions
  const shouldShowUpgradePrompt = warningLevel !== 'none';

  // Automatic refetch on trial expiry (every 5 minutes if trial is active)
  useEffect(() => {
    if (!isTrialActive) return undefined;

    const interval = setInterval(
      () => {
        refetch();
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearInterval(interval);
  }, [isTrialActive, refetch]);

  return {
    // Trial status data
    trialStatus,
    isTrialActive,
    isTrialExpired,
    daysRemaining,
    trialEndDate,
    planName,

    // Warning system
    warningLevel,
    warningMessage,
    shouldShowUpgradePrompt,

    // Loading states
    isLoading,
    error,
    refetch,

    // Helper functions
    hasTrialWarning: warningLevel === 'warning',
    hasTrialUrgent: warningLevel === 'urgent',
    hasTrialExpired: warningLevel === 'expired',
  };
};

/**
 * Hook for trial countdown display
 */
export const useTrialCountdown = () => {
  const { trialEndDate, daysRemaining, isTrialActive } = useTrialStatus();

  // Calculate hours and minutes remaining
  const getTimeRemaining = () => {
    if (!trialEndDate || !isTrialActive) {
      return { days: 0, hours: 0, minutes: 0, total: 0 };
    }

    const now = new Date().getTime();
    const end = trialEndDate.getTime();
    const difference = end - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, total: 0 };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    return { days, hours, minutes, total: difference };
  };

  const timeRemaining = getTimeRemaining();

  // Format countdown display
  const formatCountdown = (): string => {
    const { days, hours, minutes } = timeRemaining;

    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  return {
    timeRemaining,
    countdownText: formatCountdown(),
    isExpired: timeRemaining.total <= 0,
    daysRemaining,
  };
};

export default useTrialStatus;
